'use client'

import { createContext, useContext, useEffect, useState } from 'react'
import { useParams, usePathname } from 'next/navigation'
import { useTranslations } from 'next-intl'
import { useFeatureFlags } from '@node-space/hooks'
import { toast } from '@node-space/ui-components'
import { getToken } from '@/app/lib/api-client'
import { Centrifuge } from 'centrifuge'
import { TransportEndpoint } from 'centrifuge/build/types'
import { PaymentSummary } from '@/types/payment'
import { TOAST_DURATION } from '@/utils/constants'
import { isValidUuid } from '@/utils/helpers'

const isTestMode = process.env.NEXT_PUBLIC_ENABLE_DEBUG_WEB_SOCKET === 'enabled'

type WebSocketPaymentSummary = {
  event: string
  source: string
  data: PaymentSummary
}

interface PaymentStatusWebSocketContextProps {
  webSocketPaymentSummary: WebSocketPaymentSummary | null
  isWebSocketConnected: boolean
}

const WebSocketPaymentSummaryContext = createContext<PaymentStatusWebSocketContextProps>({
  webSocketPaymentSummary: null,
  isWebSocketConnected: false,
})

export const WebSocketPaymentSummaryContextProvider = ({
  children,
}: {
  children: React.ReactNode
}) => {
  const params = useParams()
  const pathname = usePathname()
  const t = useTranslations()
  const { enableWebsocket, enableWebSocketChannel, enableWebSocketChannelToast } = useFeatureFlags()
  const uuid = params?.uuid as string
  const pathNameArray = pathname?.split('/')
  const lastItem = pathNameArray?.slice(-1)?.[0] || ''
  const initialPaymentFlow = pathname?.endsWith(lastItem) && isValidUuid(lastItem)
  const isChannel = pathname?.includes('channel')

  const [webSocketPaymentSummary, setWebSocketPaymentSummary] =
    useState<WebSocketPaymentSummary | null>(null)
  const [token, setToken] = useState<string | null>(null)
  const [isWebSocketConnected, setIsWebSocketConnected] = useState<boolean>(false)

  useEffect(() => {
    let restrictAccessOnRoute

    if (initialPaymentFlow || pathname?.endsWith('expired') || !enableWebsocket) {
      if (isChannel && enableWebSocketChannel) {
        restrictAccessOnRoute = false
      } else {
        restrictAccessOnRoute = true
      }
    }

    if (restrictAccessOnRoute) return

    const fetchToken = async () => {
      try {
        const response = await getToken(uuid)
        const { token } = response

        setToken(token)
      } catch (error) {
        // eslint-disable-next-line no-console
        isTestMode && console.debug('getToken Error', error)
        setToken(null)
      }
    }

    fetchToken()
  }, [uuid, initialPaymentFlow, enableWebsocket, pathname, isChannel, enableWebSocketChannel])

  useEffect(() => {
    if (!isValidUuid(uuid) || token == null) {
      return
    }

    const transports: Array<TransportEndpoint> = [
      {
        transport: 'websocket',
        endpoint: process.env.NEXT_PUBLIC_WS_URL ?? '',
      },
      {
        transport: 'http_stream',
        endpoint: process.env.NEXT_PUBLIC_HTTP_STREAM_URL ?? '',
      },
    ]

    const centrifugeClient = new Centrifuge(transports, { token })

    if (!enableWebsocket) {
      disconnect(centrifugeClient)
      return
    }

    centrifugeClient.on('error', errorContext => {
      isTestMode && console.error('centrifugeClient Error', errorContext)
      disconnect(centrifugeClient)
    })

    centrifugeClient.on('connected', connectedContext => {
      // eslint-disable-next-line no-console
      isTestMode && console.log('connected', connectedContext)
      setIsWebSocketConnected(true)
    })

    const channelName = `webhook#${uuid}`

    let subscription = centrifugeClient.getSubscription(channelName)

    if (!subscription) {
      subscription = centrifugeClient.newSubscription(channelName)
    }

    subscription.on('subscribed', subscribedContext => {
      // eslint-disable-next-line no-console
      isTestMode && console.log('subscribed', subscribedContext)
    })

    subscription.on('publication', publicationContext => {
      // eslint-disable-next-line no-console
      isTestMode && console.log('publication', publicationContext)
      const { data } = publicationContext
      updatePaymentSummary(data)
    })

    subscription.on('unsubscribed', unsubscribedContext => {
      // eslint-disable-next-line no-console
      isTestMode && console.log('disconnected', unsubscribedContext)
      disconnect(centrifugeClient)
    })

    subscription.on('error', subscribedErrorContext => {
      isTestMode && console.error('subscribedErrorContext Error', subscribedErrorContext)
      disconnect(centrifugeClient)
    })

    centrifugeClient.connect()
    subscription.subscribe()

    return () => {
      centrifugeClient.disconnect()
    }
  }, [uuid, token, enableWebsocket])

  useEffect(() => {
    if (webSocketPaymentSummary && isChannel && enableWebSocketChannelToast) {
      toast({
        header: t(webSocketPaymentSummary?.event),
        variant: 'success',
        duration: TOAST_DURATION,
      })
    }
  }, [enableWebSocketChannelToast, isChannel, t, webSocketPaymentSummary])

  const disconnect = (centrifugeClient: Centrifuge) => {
    // eslint-disable-next-line no-console
    isTestMode && console.log('Centrifuge Client disconnected')
    setIsWebSocketConnected(false)
    centrifugeClient.disconnect()
  }

  const updatePaymentSummary = (publicationContextData: WebSocketPaymentSummary) => {
    setWebSocketPaymentSummary(publicationContextData)
  }

  return (
    <WebSocketPaymentSummaryContext.Provider
      value={{ webSocketPaymentSummary, isWebSocketConnected }}
    >
      {children}
    </WebSocketPaymentSummaryContext.Provider>
  )
}

export const useWebSocketPaymentSummaryContext = () => useContext(WebSocketPaymentSummaryContext)
